import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'

// Colours match ManualListing.jsx and Testing.jsx.
const colors = {
  charcoal: '#222725',
  grey100: '#F8F9F8',
  grey300: '#D6D3D1',
  grey500: '#78716C',
  amberLight: '#FFF9E6',
  redLight: '#FCEEED',
  crimson: '#E5625E',
  blueLight: '#EDF3F6'
}

const inputStyle = {
  flex: 1, minWidth: 0, padding: '14px 16px', border: `1px solid ${colors.grey300}`,
  borderRadius: '10px', fontSize: '15px', outline: 'none'
}

const buttonStyle = {
  padding: '12px 16px', borderRadius: '10px', border: `1px solid ${colors.grey300}`,
  background: '#FFFFFF', fontWeight: 600, cursor: 'pointer'
}

/** Mirrors the server's cap (workers manualListingLink.js) for a listing with no trade line. */
export const MAX_MANUAL_LISTING_QUANTITY = 100

const poundsFromCents = (cents) => (cents == null ? null : `£${(cents / 100).toFixed(2)}`)

/**
 * The optional trade line a manual listing came from. With it, photography
 * completion links the Shopify product to its quote item (unit economics D36).
 *
 * Staff can instead pick an extra: an item sent with the trade but not on its
 * quote, never paid for (spec docs/specs_20261021_trade_extra_items.md). Not
 * offered on a consignment trade.
 *
 * Or staff can say the item is a return (Trac #1333, spec
 * docs/specs_20261022_return_relist.md): they find the sale it came from, and
 * the server takes the trade link and cost from that old product.
 *
 * `value` is null, `{ kind: 'line', quoteId, reference, line }`,
 * `{ kind: 'extra', quoteId, reference }` or `{ kind: 'return', item }`;
 * `onChange` gets the same.
 */
export default function TradeLinkPicker({ value, onChange }) {
  const [findingReturn, setFindingReturn] = useState(false)
  const [reference, setReference] = useState('')
  const [trade, setTrade] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const findTrade = async () => {
    const ref = reference.trim()
    if (!ref) return
    setLoading(true)
    setError(null)
    setTrade(null)
    try {
      setTrade(await api.get(`/api/warehouse/manual-listing/trade?ref=${encodeURIComponent(ref)}`))
    } catch (err) {
      setError(err.message || 'Could not load the trade')
    } finally {
      setLoading(false)
    }
  }

  if (value) {
    return (
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>From a trade</div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          padding: '14px 16px', background: colors.blueLight, borderRadius: '10px'
        }}>
          {value.kind === 'return' ? (
            <div>
              <div style={{ fontWeight: 600 }}>Return of {value.item.title || `product ${value.item.shopify_product_id}`}</div>
              <div style={{ fontSize: '12px', color: colors.grey500 }}>{saleLine(value.item)}</div>
            </div>
          ) : value.kind === 'extra' ? (
            <div>
              <div style={{ fontWeight: 600 }}>{value.reference}: extra item, not on the quote</div>
              <div style={{ fontSize: '12px', color: colors.grey500 }}>Never paid for, so its buy price is £0</div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600 }}>{value.reference}: {value.line.name}</div>
              <div style={{ fontSize: '12px', color: colors.grey500 }}>
                {value.line.quantity} bought · {value.line.condition || 'no condition'}
                {value.line.buyPriceCents != null && ` · ${poundsFromCents(value.line.buyPriceCents)} each`}
              </div>
            </div>
          )}
          <button type="button" style={buttonStyle} onClick={() => onChange(null)}>
            {value.kind === 'return' ? 'Remove return' : 'Remove trade'}
          </button>
        </div>
      </div>
    )
  }

  if (findingReturn) {
    return (
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Find the sale this return came from</div>
        <ReturnSalePicker
          onPick={item => { setFindingReturn(false); onChange({ kind: 'return', item }) }}
          onCancel={() => setFindingReturn(false)}
        />
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <label htmlFor="manual-listing-trade-ref" style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
        Trade reference (optional)
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          id="manual-listing-trade-ref"
          type="text"
          value={reference}
          onChange={e => setReference(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') findTrade() }}
          placeholder="e.g. TI-32A2F"
          style={inputStyle}
        />
        <button type="button" style={buttonStyle} disabled={loading || !reference.trim()} onClick={findTrade}>
          {loading ? 'Finding…' : 'Find trade'}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setFindingReturn(true)}
        style={{ ...buttonStyle, marginTop: '8px', width: '100%', textAlign: 'left' }}
      >
        This is a return (find the sale)
      </button>

      {error && (
        <div style={{ marginTop: '8px', padding: '10px 14px', background: colors.redLight, color: colors.crimson, borderRadius: '10px' }}>
          {error}
        </div>
      )}

      {trade && trade.lines.length === 0 && (
        <div style={{ marginTop: '8px', color: colors.grey500 }}>This trade has no lines that can be listed.</div>
      )}

      {trade && trade.lines.length > 0 && (
        <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
          <div style={{ fontSize: '13px', color: colors.grey500 }}>Pick the line this item came from:</div>
          {trade.lines.map(line => (
            <button
              key={line.quoteItemId}
              type="button"
              aria-label={line.name}
              onClick={() => onChange({ kind: 'line', quoteId: trade.quoteId, reference: trade.reference, line })}
              style={{ ...buttonStyle, textAlign: 'left', fontWeight: 400 }}
            >
              <div style={{ fontWeight: 600 }}>{line.name}</div>
              <div style={{ fontSize: '12px', color: colors.grey500 }}>
                {line.quantity} bought · {line.condition || 'no condition'}
                {line.buyPriceCents != null && ` · ${poundsFromCents(line.buyPriceCents)} each`}
              </div>
              {line.linkedProducts > 0 && (
                <div style={{ marginTop: '6px', fontSize: '12px', padding: '4px 8px', background: colors.amberLight, borderRadius: '6px' }}>
                  {line.linkedProducts} Shopify product{line.linkedProducts === 1 ? '' : 's'} already linked to this line. Check you are not listing the same unit twice.
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {trade && (trade.isListingService ? (
        <div style={{ marginTop: '8px', fontSize: '13px', color: colors.grey500 }}>
          Extra items are not allowed on consignment trades.
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onChange({ kind: 'extra', quoteId: trade.quoteId, reference: trade.reference })}
          style={{ ...buttonStyle, marginTop: '8px', width: '100%', textAlign: 'left' }}
        >
          Extra item from this trade (not on the quote)
        </button>
      ))}
    </div>
  )
}

const saleLine = (item) => [
  item.order_name && `Order ${item.order_name}`,
  item.channel,
  item.sold_at && `sold ${item.sold_at}`,
  item.condition
].filter(Boolean).join(' · ')

/** The link fields of a manual listing request. A return sends only its old product. */
export function linkRequestFields(link) {
  if (!link) return {}
  if (link.kind === 'return') return { returnProductId: link.item.shopify_product_id }
  if (link.kind === 'extra') return { dealId: link.quoteId }
  return { dealId: link.quoteId, quoteItemId: link.line.quoteItemId }
}

function SaleSummary({ item }) {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      {item.image_url && (
        <img src={item.image_url} alt="" style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }} />
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{item.title || `Product ${item.shopify_product_id}`}</div>
        <div style={{ fontSize: '12px', color: colors.grey500 }}>{saleLine(item)}</div>
        {item.refunded && (
          <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '12px', padding: '2px 8px', background: colors.amberLight, borderRadius: '6px' }}>
            Refunded
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Finds the sale a returned item came from: an order number or part of the
 * item name. A pick is confirmed against the old listing before it counts.
 */
export function ReturnSalePicker({ onPick, onCancel }) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState(null)
  const [picked, setPicked] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const findSale = async () => {
    const q = query.trim()
    if (!q) return
    setLoading(true)
    setError(null)
    setItems(null)
    try {
      setItems((await api.get(`/api/warehouse/manual-listing/returns?q=${encodeURIComponent(q)}`)).items)
    } catch (err) {
      setError(err.message || 'Could not search the sales')
    } finally {
      setLoading(false)
    }
  }

  if (picked) {
    return (
      <div style={{ padding: '14px 16px', background: colors.blueLight, borderRadius: '10px' }}>
        <div style={{ fontSize: '13px', marginBottom: '8px' }}>Check this is the item in front of you:</div>
        <SaleSummary item={picked} />
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button type="button" style={buttonStyle} onClick={() => onPick(picked)}>Yes, this item</button>
          <button type="button" style={buttonStyle} onClick={() => setPicked(null)}>Pick again</button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <label htmlFor="return-sale-query" style={{ display: 'block', fontSize: '13px', color: colors.grey500, marginBottom: '6px' }}>
        Order number or item name
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          id="return-sale-query"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') findSale() }}
          placeholder="e.g. 2875 or Tascam DR-60"
          style={inputStyle}
        />
        <button type="button" style={buttonStyle} disabled={loading || !query.trim()} onClick={findSale}>
          {loading ? 'Finding…' : 'Find sale'}
        </button>
        <button type="button" style={buttonStyle} onClick={onCancel}>Cancel</button>
      </div>

      {error && (
        <div style={{ marginTop: '8px', padding: '10px 14px', background: colors.redLight, color: colors.crimson, borderRadius: '10px' }}>
          {error}
        </div>
      )}

      {items && items.length === 0 && (
        <div style={{ marginTop: '8px', color: colors.grey500 }}>No sale found. Try the order number or another part of the name.</div>
      )}

      {items && items.length > 0 && (
        <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
          {items.map(item => (
            <button
              key={item.sale_line_id}
              type="button"
              aria-label={item.title || item.shopify_product_id}
              onClick={() => setPicked(item)}
              style={{ ...buttonStyle, textAlign: 'left', fontWeight: 400 }}
            >
              <SaleSummary item={item} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * A queued return on the photography screen: staff can change the sale or
 * remove the link until the shoot completes. `onChanged` gets the new old
 * product id, or null.
 */
export function QueuedReturn({ item, onChanged }) {
  const [changing, setChanging] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState(null)

  if (item.source !== 'manual_listing' || !item.source_return_product_id || item.status === 'completed') return null

  const save = async (returnProductId) => {
    setError(null)
    try {
      await api.patch(`/api/warehouse/photography/${item.photography_queue_id}`, { returnProductId })
      setChanging(false)
      setRemoving(false)
      onChanged(returnProductId)
    } catch (err) {
      setError(err.message || 'Could not change the return')
    }
  }

  return (
    <div style={{ padding: '14px 16px', background: colors.blueLight, borderRadius: '10px' }}>
      {changing ? (
        <ReturnSalePicker onPick={sold => save(sold.shopify_product_id)} onCancel={() => setChanging(false)} />
      ) : removing ? (
        <div>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Remove the return link?</div>
          <div style={{ fontSize: '13px', marginBottom: '10px' }}>
            The item will list with no trade, and you cannot add the link back here.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={buttonStyle} onClick={() => save(null)}>Yes, remove it</button>
            <button type="button" style={buttonStyle} onClick={() => setRemoving(false)}>Keep it</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontWeight: 600 }}>Return of product {item.source_return_product_id}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={buttonStyle} onClick={() => setChanging(true)}>Change</button>
            <button type="button" style={buttonStyle} onClick={() => setRemoving(true)}>Remove return link</button>
          </div>
        </div>
      )}
      {error && <div style={{ marginTop: '8px', color: colors.crimson }}>{error}</div>}
    </div>
  )
}

/**
 * The submit button for a manual listing. Without a trade, the first press
 * stops and asks staff to check; the second press submits.
 *
 * `children` is the button label, or a function `(press) => <button>` so a
 * screen can keep its own button style.
 */
export function NoTradeSubmitGuard({ hasTrade, onSubmit, onLinkTrade, disabled = false, children }) {
  const [warned, setWarned] = useState(false)
  // Linking or removing a trade starts the check again.
  useEffect(() => { setWarned(false) }, [hasTrade])

  const press = () => {
    if (!hasTrade && !warned) {
      setWarned(true)
      return
    }
    onSubmit()
  }

  return (
    <div>
      {!hasTrade && warned && (
        <div role="alert" style={{
          marginBottom: '12px', padding: '14px 16px', background: colors.amberLight,
          borderRadius: '10px', color: colors.charcoal
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>You have not linked this listing to a trade.</div>
          <div style={{ fontSize: '14px' }}>
            Please check the paperwork. If the item has no trade, press the button again to continue.
          </div>
          {onLinkTrade && (
            <button type="button" style={{ ...buttonStyle, marginTop: '10px' }} onClick={onLinkTrade}>
              Link a trade
            </button>
          )}
        </div>
      )}
      {typeof children === 'function'
        ? children(press)
        : <button type="button" disabled={disabled} onClick={press}>{children}</button>}
    </div>
  )
}
